import { useEffect, useMemo, useState } from "react";
import type { AttendeeSchoolProposalSet, AttendeeSession } from "@verity-connect/contracts";
import { useAttendeeStateEpoch } from "./cross-device-state";

/**
 * Loads School suggestions beside the attendee's own schedule without joining the two.
 * A missing or failed suggestion request leaves personal planning fully usable.
 */
export function useSchoolProposals(session: AttendeeSession | null, eventSlug: string, online: boolean) {
  const signal = useAttendeeStateEpoch();
  const [proposalSet, setProposalSet] = useState<AttendeeSchoolProposalSet | null>(null);

  useEffect(() => {
    setProposalSet(null);
    if (!session || !online) return;
    const controller = new AbortController();
    void fetch(`/api/attendee/events/${encodeURIComponent(eventSlug)}/school-proposals`, {
      credentials: "same-origin",
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return null;
      return response.json() as Promise<AttendeeSchoolProposalSet | null>;
    }).then((value) => {
      if (!controller.signal.aborted) setProposalSet(value);
    }).catch(() => {
      // A School suggestion is helpful context. It never blocks the attendee's schedule.
    });
    return () => controller.abort();
  }, [eventSlug, online, session?.identityId, signal.epoch]);

  const proposedItemIds = useMemo(
    () => new Set(proposalSet?.proposals.flatMap((proposal) => proposal.itemId ? [proposal.itemId] : []) ?? []),
    [proposalSet],
  );
  return { proposalSet, proposedItemIds };
}
